import React, { createContext, useCallback, useContext, useState } from 'react';
import { X } from 'lucide-react';

// A non-blocking, right-docked image viewer. Any question image (question text, options,
// explanation, passage, matching diagram — anywhere) can call openImage(src) to enlarge it in
// a fixed panel on the RIGHT of the screen. The panel does NOT cover the whole viewport and has
// NO backdrop, so the rest of the page (the question + answer inputs) stays fully interactive —
// the user can read/zoom the image on the right while answering.
const ImageViewerContext = createContext({ openImage: () => {} });

export const useImageViewer = () => useContext(ImageViewerContext);

// A fixed-size inline image thumbnail with a "Click to view in full mode" hint. Used anywhere a
// question / option / explanation image appears so every image renders at the SAME easy-to-see size
// (regardless of its natural dimensions) and opens the right-docked viewer in full mode on click.
// The hint is a <span role="button"> (not a <button>) so it's valid inside option <button>s.
export function ClickToViewImage({ src, imgStyle, containerStyle }) {
    const { openImage } = useImageViewer();
    if (!src) return null;
    const open = (e) => { e.stopPropagation(); openImage(src); };
    return (
        <div style={{ marginBottom: '0.5rem', ...containerStyle }}>
            <span
                role="button"
                tabIndex={0}
                onClick={open}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') open(e); }}
                style={{ display: 'inline-block', color: '#7c3aed', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', marginBottom: 3 }}
            >
                🔍 Click to view in full mode
            </span>
            <img
                src={src}
                alt=""
                onClick={open}
                style={{
                    display: 'block', height: 240, width: 'auto', maxWidth: '100%',
                    objectFit: 'contain', cursor: 'zoom-in', ...imgStyle,
                }}
            />
        </div>
    );
}

export function ImageViewerProvider({ children }) {
    const [src, setSrc] = useState(null);
    const [zoomed, setZoomed] = useState(false);

    // Open in FULL (fit) mode by default — the whole image scaled to fill the viewer. Clicking the
    // image toggles to fit-width.
    const openImage = useCallback((s) => {
        if (s) { setSrc(s); setZoomed(true); }
    }, []);
    const close = useCallback(() => setSrc(null), []);

    return (
        <ImageViewerContext.Provider value={{ openImage }}>
            {children}
            {src && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: 'min(48vw, 620px)',
                        background: '#fff',
                        boxShadow: '-3px 0 20px rgba(0,0,0,0.28)',
                        borderLeft: '1px solid #e5e7eb',
                        zIndex: 3000,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.5rem 0.75rem',
                            borderBottom: '1px solid #e5e7eb',
                            background: '#f9fafb',
                            flexShrink: 0,
                        }}
                    >
                        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                            Image — click it to {zoomed ? 'fit width' : 'fit full size'}
                        </span>
                        <button
                            type="button"
                            onClick={close}
                            aria-label="Close image"
                            title="Close image"
                            style={{
                                border: 'none',
                                background: '#ef4444',
                                color: '#fff',
                                width: 30,
                                height: 30,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                flexShrink: 0,
                            }}
                        >
                            <X size={18} />
                        </button>
                    </div>
                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            padding: '0.75rem',
                            background: '#f3f4f6',
                            ...(zoomed
                                ? { display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }
                                : { overflow: 'auto', textAlign: 'center' }),
                        }}
                    >
                        <img
                            src={src}
                            alt=""
                            onClick={() => setZoomed((z) => !z)}
                            style={zoomed
                                // "Full size" — scale the whole image (up or down) to fill the viewer's
                                // full width and height, aspect ratio preserved (nothing overflows).
                                ? { width: '100%', height: '100%', objectFit: 'contain', cursor: 'zoom-out', borderRadius: 4 }
                                : { maxWidth: '100%', height: 'auto', cursor: 'zoom-in', borderRadius: 4 }}
                        />
                    </div>
                </div>
            )}
        </ImageViewerContext.Provider>
    );
}
